import { getEntities } from "../dao/common";
import { errorMessageHandler } from "./error";

/**
 * Configuration object for entity number pre-filling
 * @typedef {Object} EntityNumberConfig
 * @property {string} apiEndpoint - The API endpoint for the entity (e.g., '/systems', '/system-groups')
 * @property {string} numberField - The field name for the number (usually 'number')
 * @property {Object} dependencies - Object containing dependency values
 * @property {Function} createQueryParams - Function to create query parameters for the API call
 * @property {Function} createInitialState - Function to create the initial state with pre-filled values
 */

/**
 * Utility function to handle number pre-filling for entities in create mode
 *
 * @param {EntityNumberConfig} config - Configuration object
 * @param {AbortController} abortController - Abort controller for the request
 * @param {Function} setEntity - State setter function for the entity
 * @param {Function} setIsLoading - State setter function for loading state
 * @param {Function} setLatestNumber - State setter function for the latest number (optional)
 * @returns {Promise} Promise that resolves when the operation is complete
 */
export const handleEntityNumberPreFill = async (
  {
    apiEndpoint,
    numberField = "number",
    dependencies,
    createQueryParams,
    createInitialState,
  },
  abortController,
  setEntity,
  setIsLoading,
  setLatestNumber
) => {
  try {
    // Build query parameters for fetching the latest number
    const queryParams = {
      limit: 1,
      offset: 0,
      overview: true,
      order: `${numberField} desc`,
      ...createQueryParams(dependencies),
    };

    // Fetch the latest entity to get the highest number
    const response = await getEntities(
      apiEndpoint,
      queryParams,
      abortController
    );
    const entities = response.response || [];

    let nextNumber = 1; // Default to 1 if no entities exist
    if (entities.length > 0 && entities[0][numberField]) {
      nextNumber = parseInt(entities[0][numberField]) + 1;
    }

    // Set the latest number if setter is provided
    if (setLatestNumber) {
      setLatestNumber(nextNumber);
    }

    // Create the initial state with pre-filled values
    const initialState = createInitialState(dependencies, nextNumber);
    setEntity(initialState);
  } catch (error) {
    errorMessageHandler(error);

    // Still set the entity with dependencies even if fetching latest number fails
    const fallbackState = createInitialState(dependencies, null);
    setEntity(fallbackState);
  } finally {
    setIsLoading(false);
  }
};

/**
 * Factory function to create entity-specific number pre-fill handlers
 *
 * @param {Object} config - Base configuration for the entity
 * @returns {Function} Configured pre-fill handler
 */
export const createEntityNumberPreFillHandler = (config) => {
  return (
    dependencies,
    abortController,
    setEntity,
    setIsLoading,
    setLatestNumber
  ) => {
    return handleEntityNumberPreFill(
      {
        ...config,
        dependencies,
        createQueryParams: config.createQueryParams || (() => ({})),
        createInitialState: config.createInitialState || (() => ({})),
      },
      abortController,
      setEntity,
      setIsLoading,
      setLatestNumber
    );
  };
};

/**
 * Pre-configured handlers for common entities
 */

// Drum pre-fill handler
export const handleDrumNumberPreFill = createEntityNumberPreFillHandler({
  apiEndpoint: "/drums",
  numberField: "number",
  createQueryParams: (dependencies) => ({
    ...(dependencies?.objectId && { object_ids: dependencies.objectId }),
  }),
  createInitialState: (dependencies, nextNumber) => ({
    object_id: dependencies?.objectId || "",
    ...(nextNumber && { number: nextNumber }),
  }),
});
